import React from "react";
import {
  Alert,
  Box,
  ButtonBase,
  Checkbox,
  Chip,
  Divider,
  Grid,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";

const BRAND = { orange: "#F35A3A", dark: "var(--wp-panel-text-primary, #19242F)" };

const PRESETS = {
  product: {
    sellable: true,
    purchasable: true,
    stockTracked: true,
    reservable: false,
    shippable: true,
    publicVisible: true,
    expenseEnabled: false,
    internalConsumptionEnabled: false,
  },
  service: {
    sellable: true,
    purchasable: false,
    stockTracked: false,
    reservable: false,
    shippable: false,
    publicVisible: true,
    expenseEnabled: false,
    internalConsumptionEnabled: false,
  },
  supply: {
    sellable: false,
    purchasable: true,
    stockTracked: true,
    reservable: false,
    shippable: false,
    publicVisible: false,
    expenseEnabled: true,
    internalConsumptionEnabled: true,
  },
  expense: {
    sellable: false,
    purchasable: true,
    stockTracked: false,
    reservable: false,
    shippable: false,
    publicVisible: false,
    expenseEnabled: true,
    internalConsumptionEnabled: false,
  },
  asset: {
    sellable: false,
    purchasable: true,
    stockTracked: false,
    reservable: false,
    shippable: false,
    publicVisible: false,
    expenseEnabled: true,
    internalConsumptionEnabled: false,
  },
  charge: {
    sellable: true,
    purchasable: true,
    stockTracked: false,
    reservable: false,
    shippable: false,
    publicVisible: false,
    expenseEnabled: true,
    internalConsumptionEnabled: false,
  },
};

export function capabilitiesForKind(kind) {
  return { ...(PRESETS[kind] || PRESETS.product) };
}

function CapabilitySwitch({ label, detail, onDetail, offDetail, checked, onChange, compact = false }) {
  const explanation = checked
    ? (onDetail || detail || "Esta función está activa.")
    : (offDetail || "Al desactivarlo, esta función deja de aplicarse a este ítem.");

  return (
    <ButtonBase
      type="button"
      onClick={() => onChange(!checked)}
      aria-pressed={checked}
      sx={{
        width: "100%",
        height: "100%",
        display: "block",
        textAlign: "left",
        p: compact ? 1.05 : 1.25,
        borderRadius: 2.5,
        bgcolor: checked ? "rgba(243,90,58,.07)" : "rgba(var(--wp-panel-ink-rgb, 25,36,47),0.025)",
        border: "1px solid",
        borderColor: checked ? "rgba(243,90,58,.28)" : "rgba(var(--wp-panel-ink-rgb, 25,36,47),0.07)",
        transition: "transform .16s ease, border-color .16s ease, box-shadow .16s ease, background-color .16s ease",
        "&:hover": {
          transform: "translateY(-1px)",
          borderColor: checked ? "rgba(243,90,58,.42)" : "rgba(var(--wp-panel-ink-rgb, 25,36,47),.14)",
          boxShadow: "0 8px 20px rgba(var(--wp-panel-shadow-rgb, 25,36,47),.06)",
        },
      }}
    >
      <Stack direction="row" spacing={0.8} alignItems="flex-start">
        <Checkbox
          checked={checked}
          tabIndex={-1}
          disableRipple
          inputProps={{ "aria-hidden": true }}
          sx={{
            p: 0.1,
            mt: 0.05,
            pointerEvents: "none",
            color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.34)",
            "&.Mui-checked": { color: BRAND.orange },
          }}
        />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Stack direction="row" spacing={0.7} alignItems="center" justifyContent="space-between">
            <Typography sx={{ fontWeight: 900, color: BRAND.dark, lineHeight: 1.15, fontSize: compact ? 13.2 : 14 }}>
              {label}
            </Typography>
            <Chip
              size="small"
              label={checked ? "Sí" : "No"}
              sx={{
                height: 20,
                fontWeight: 950,
                fontSize: 10,
                bgcolor: checked ? "rgba(243,90,58,.13)" : "rgba(var(--wp-panel-ink-rgb, 25,36,47),.055)",
                color: BRAND.dark,
              }}
            />
          </Stack>
          <Typography
            variant="caption"
            sx={{
              display: "block",
              mt: 0.3,
              color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.66)",
              lineHeight: 1.28,
            }}
          >
            {explanation}
          </Typography>
        </Box>
      </Stack>
    </ButtonBase>
  );
}

function CostMetric({ label, value, detail, currency = "ARS" }) {
  const amount = Number(value || 0);
  const formatted = Number.isFinite(amount)
    ? amount.toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : "0,00";
  return (
    <Box
      sx={{
        p: 1.05,
        borderRadius: 2.3,
        bgcolor: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.03)",
        border: "1px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),.065)",
        minWidth: 0,
      }}
    >
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 850 }}>{label}</Typography>
      <Typography sx={{ mt: 0.2, fontWeight: 1000, color: BRAND.dark, lineHeight: 1.05 }}>
        {currency === "ARS" ? "$" : currency} {formatted}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.35, lineHeight: 1.2 }}>
        {detail}
      </Typography>
    </Box>
  );
}

function AccountSelect({ label, value, onChange, accounts, allowedTypes }) {
  const available = (accounts || []).filter(
    (account) => account.active !== false && (!allowedTypes?.length || allowedTypes.includes(account.type)),
  );

  return (
    <TextField select label={label} value={value || ""} onChange={onChange} fullWidth>
      {available.map((account) => (
        <MenuItem key={account.code} value={account.code}>
          {account.name}
        </MenuItem>
      ))}
    </TextField>
  );
}


export default function ItemOperationalFields({
  form,
  setForm,
  canManageCosts = false,
  canManageAccounting = false,
  accounts = [],
  taxProfiles = [],
  membershipsEnabled = false,
  compact = false,
  section = "all",
}) {
  const change = (field, value) => setForm((current) => ({ ...current, [field]: value }));
  const changeCapability = (field, value) =>
    setForm((current) => ({
      ...current,
      [field]: value,
      ...(field === "reservable" ? { requiresBooking: value } : {}),
      ...(field === "shippable" ? { requiresShipping: value } : {}),
    }));
  const activeTaxProfiles = (taxProfiles || []).filter((profile) => profile.active !== false);
  const selectedTax = activeTaxProfiles.find((profile) => profile.code === form.taxProfileCode);
  const itemKind = String(form.erpItemKind || form.itemType || "product").toLowerCase();
  const supportsInventory = itemKind === "product" || itemKind === "supply";
  const supportsShipping = itemKind === "product";
  const tracksInventory = supportsInventory && form.stockTracked === true;
  const hasUsefulCost = Number(form.averageCost || form.lastCost || form.standardCost || 0) > 0;
  const showOperations = section !== "billing";
  const showBilling = section !== "operations";
  const costCurrency = String(form.costCurrency || "ARS").toUpperCase();

  return (
    <Stack spacing={compact ? 1.25 : 1.6}>
      {showOperations && canManageCosts && (form.purchasable || tracksInventory || form.expenseEnabled) ? (
        <Paper variant="outlined" sx={{ p: compact ? 1.3 : 1.55, borderRadius: 3 }}>
          <Stack spacing={1.15}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={0.7} alignItems={{ sm: "center" }} justifyContent="space-between">
              <Box>
                <Typography sx={{ fontWeight: 950, color: BRAND.dark }}>Costo interno</Typography>
                <Typography variant="caption" color="text.secondary">
                  Esto no se muestra en la web. Sirve para saber cuánto te cuesta realmente vender o reponer este ítem.
                </Typography>
              </Box>
              <Chip size="small" label="Referencia interna" sx={{ fontWeight: 900 }} />
            </Stack>

            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(2,minmax(0,1fr))" }, gap: 0.8 }}>
              <CostMetric
                label="Último costo"
                value={form.lastCost}
                currency={costCurrency}
                detail="Lo actualiza la última compra confirmada. No se edita acá."
              />
              <CostMetric
                label="Costo promedio"
                value={form.averageCost}
                currency={costCurrency}
                detail="Promedio histórico de compras. No se edita manualmente."
              />
            </Box>

            {!hasUsefulCost && form.sellable ? (
              <Alert severity="info" sx={{ py: 0.2 }}>
                Todavía no hay costos reales. Podés dejarlo así o cargar un costo de referencia para estimar margen.
              </Alert>
            ) : null}

            <Grid container spacing={1}>
              <Grid item xs={12} sm={6}>
                <TextField
                  type="number"
                  size="small"
                  label="Costo de referencia"
                  value={form.standardCost}
                  onChange={(event) => change("standardCost", event.target.value)}
                  inputProps={{ min: 0, step: "any" }}
                  helperText="Editable. Usalo como estimación inicial; las compras reales no se pisan con este valor."
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  size="small"
                  label="Costo que usa el sistema"
                  value={form.costingMethod}
                  onChange={(event) => change("costingMethod", event.target.value)}
                  helperText="Define qué costo toma WebProfit como referencia operativa."
                  fullWidth
                >
                  <MenuItem value="last_cost">Último costo</MenuItem>
                  <MenuItem value="average_cost">Costo promedio</MenuItem>
                  <MenuItem value="standard_cost">Costo de referencia</MenuItem>
                </TextField>
              </Grid>
            </Grid>
          </Stack>
        </Paper>
      ) : null}

      {showOperations ? (
        <Paper variant="outlined" sx={{ p: compact ? 1.25 : 1.5, borderRadius: 3 }}>
          <Stack spacing={1.1}>
            <Box>
              <Typography sx={{ fontWeight: 950, color: BRAND.dark }}>Cómo participa en tu negocio</Typography>
              <Typography variant="caption" color="text.secondary">
                Tocá cada tarjeta. Te mostramos inmediatamente qué pasa si queda activa o desactivada.
              </Typography>
            </Box>
            <Divider />
            <Grid container spacing={0.85}>
              <Grid item xs={12} md={6}>
                <CapabilitySwitch
                  compact={compact}
                  label="Se puede vender"
                  checked={Boolean(form.sellable)}
                  onChange={(value) => change("sellable", value)}
                  onDetail="Aparece al registrar ventas y puede generar ingresos."
                  offDetail="No aparecerá como opción cuando alguien cargue una venta."
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <CapabilitySwitch
                  compact={compact}
                  label="Se puede comprar"
                  checked={Boolean(form.purchasable)}
                  onChange={(value) => change("purchasable", value)}
                  onDetail="Aparece al registrar compras o reposiciones."
                  offDetail="No se ofrecerá como concepto cuando registres una compra."
                />
              </Grid>
              {supportsInventory ? (
                <Grid item xs={12} md={6}>
                  <CapabilitySwitch
                    compact={compact}
                    label="Controlar stock"
                    checked={tracksInventory}
                    onChange={(value) => change("stockTracked", value)}
                    onDetail="WebProfit controla existencias. Las entradas y salidas se anotan desde Movimientos."
                    offDetail="Se puede usar sin exigir ni descontar una existencia física."
                  />
                </Grid>
              ) : null}
              {supportsShipping ? (
                <Grid item xs={12} md={6}>
                  <CapabilitySwitch
                    compact={compact}
                    label="Admite envío o entrega"
                    checked={Boolean(form.shippable ?? form.requiresShipping)}
                    onChange={(value) => changeCapability("shippable", value)}
                    onDetail="Los pedidos pueden incluir una entrega física de este producto."
                    offDetail="No se ofrecerá envío o entrega para este ítem."
                  />
                </Grid>
              ) : null}
              <Grid item xs={12} md={6}>
                <CapabilitySwitch
                  compact={compact}
                  label="Visible en la página web"
                  checked={Boolean(form.publicVisible)}
                  onChange={(value) => change("publicVisible", value)}
                  onDetail="Puede mostrarse en el catálogo público para tus clientes."
                  offDetail="Queda disponible internamente, pero no se muestra en la web."
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <CapabilitySwitch
                  compact={compact}
                  label="Se puede registrar como gasto"
                  checked={Boolean(form.expenseEnabled)}
                  onChange={(value) => change("expenseEnabled", value)}
                  onDetail="Puede elegirse como concepto cuando registrás un gasto pagado."
                  offDetail="No aparecerá como concepto de gasto."
                />
              </Grid>
              {tracksInventory ? (
                <Grid item xs={12} md={6}>
                  <CapabilitySwitch
                    compact={compact}
                    label="Permite consumo interno"
                    checked={Boolean(form.internalConsumptionEnabled)}
                    onChange={(value) => change("internalConsumptionEnabled", value)}
                    onDetail="Podés descontar unidades para uso interno sin registrarlas como venta."
                    offDetail="Toda salida deberá explicarse con otro tipo de movimiento."
                  />
                </Grid>
              ) : null}
            </Grid>
          </Stack>
        </Paper>
      ) : null}

      {showOperations && membershipsEnabled ? (
        <Paper variant="outlined" sx={{ p: compact ? 1.25 : 1.5, borderRadius: 3, borderColor: form.membershipEligible ? "rgba(243,90,58,.28)" : undefined }}>
          <Stack spacing={1.1}>
            <Typography sx={{ fontWeight: 950, color: BRAND.dark }}>Membresía</Typography>
            <CapabilitySwitch
              compact={compact}
              label="Puede usarse como membresía"
              checked={Boolean(form.membershipEligible)}
              onChange={(value) => setForm((current) => ({
                ...current,
                membershipEligible: value,
                membershipPeriodValue: current.membershipPeriodValue || "1",
                membershipPeriodUnit: current.membershipPeriodUnit || "months",
                membershipReferencePrice: value && (current.membershipReferencePrice === "" || current.membershipReferencePrice === null || current.membershipReferencePrice === undefined)
                  ? String(current.price ?? "")
                  : current.membershipReferencePrice,
              }))}
              onDetail="Este ítem puede funcionar como plan recurrente para nuevas asignaciones."
              offDetail="Se comporta como un ítem normal, sin renovaciones recurrentes."
            />
            {form.membershipEligible ? (
              <>
                <Grid container spacing={1}>
                  <Grid item xs={12} sm={4}>
                    <TextField type="number" size="small" label="Precio por período" value={form.membershipReferencePrice} onChange={(event) => change("membershipReferencePrice", event.target.value)} inputProps={{ min: 0, step: "any" }} fullWidth />
                  </Grid>
                  <Grid item xs={6} sm={4}>
                    <TextField type="number" size="small" label="Cada" value={form.membershipPeriodValue} onChange={(event) => change("membershipPeriodValue", event.target.value)} inputProps={{ min: 1, max: 10000, step: 1 }} fullWidth />
                  </Grid>
                  <Grid item xs={6} sm={4}>
                    <TextField select size="small" label="Periodicidad" value={form.membershipPeriodUnit} onChange={(event) => change("membershipPeriodUnit", event.target.value)} fullWidth>
                      <MenuItem value="days">Días</MenuItem>
                      <MenuItem value="weeks">Semanas</MenuItem>
                      <MenuItem value="months">Meses</MenuItem>
                      <MenuItem value="years">Años</MenuItem>
                    </TextField>
                  </Grid>
                </Grid>
                <CapabilitySwitch
                  compact={compact}
                  label="Controlar usos incluidos"
                  checked={Boolean(form.membershipUsageEnabled)}
                  onChange={(value) => change("membershipUsageEnabled", value)}
                  onDetail="WebProfit controla un cupo de usos separado del stock físico."
                  offDetail="La membresía no limita usos por período."
                />
                {form.membershipUsageEnabled ? (
                  <Grid container spacing={1}>
                    <Grid item xs={12} sm={6}>
                      <TextField type="number" size="small" label="Cantidad incluida" value={form.membershipIncludedUses} onChange={(event) => change("membershipIncludedUses", event.target.value)} inputProps={{ min: 1, max: 1000000, step: 1 }} fullWidth />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField size="small" label="Nombre de la unidad" value={form.membershipUsageLabel} onChange={(event) => change("membershipUsageLabel", event.target.value)} inputProps={{ maxLength: 60 }} placeholder="usos, sesiones, accesos…" fullWidth />
                    </Grid>
                  </Grid>
                ) : null}
              </>
            ) : null}
          </Stack>
        </Paper>
      ) : null}

      {showBilling ? (
        !canManageAccounting ? (
          <Alert severity="info">
            Tu rol no administra facturación. El ítem se puede guardar igual y una persona autorizada podrá completar esta parte después.
          </Alert>
        ) : !accounts.length && !activeTaxProfiles.length ? (
          <Paper
            variant="outlined"
            sx={{
              p: { xs: 1.35, sm: 1.6 },
              borderRadius: 3,
              borderColor: "rgba(243,90,58,.20)",
              bgcolor: "rgba(243,90,58,.028)",
            }}
          >
            <Stack direction="row" spacing={1.1} alignItems="flex-start">
              <Box sx={{ width: 38, height: 38, flex: "0 0 auto", borderRadius: 2.2, display: "grid", placeItems: "center", bgcolor: "rgba(243,90,58,.12)", color: BRAND.orange }}>
                <ReceiptLongRoundedIcon sx={{ fontSize: 22 }} />
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 950, color: BRAND.dark }}>Todavía no configuraste facturación</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.3, lineHeight: 1.4 }}>
                  No bloquea la creación del ítem. Cuando el proyecto tenga cuentas y perfiles fiscales conectados, esta misma sección va a permitir elegir impuestos y cuentas sin cambiar el producto.
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.65 }}>
                  Base preparada: cuentas contables + perfil de impuestos + proveedor de comprobantes.
                </Typography>
              </Box>
            </Stack>
          </Paper>
        ) : (
          <Paper variant="outlined" sx={{ p: compact ? 1.3 : 1.55, borderRadius: 3 }}>
            <Stack spacing={1.2}>
              <Box>
                <Typography sx={{ fontWeight: 950, color: BRAND.dark }}>Cuentas e impuestos</Typography>
                <Typography variant="caption" color="text.secondary">
                  Estas decisiones se copian en cada operación para conservar el historial.
                </Typography>
              </Box>

              {accounts.length ? (
                <Grid container spacing={1}>
                  {form.sellable ? (
                    <Grid item xs={12} md={6}><AccountSelect label="Dónde registrar las ventas" value={form.salesAccountCode} onChange={(event) => change("salesAccountCode", event.target.value)} accounts={accounts} allowedTypes={["income"]} /></Grid>
                  ) : null}
                  {form.purchasable ? (
                    <Grid item xs={12} md={6}><AccountSelect label="Dónde registrar las compras" value={form.purchaseAccountCode} onChange={(event) => change("purchaseAccountCode", event.target.value)} accounts={accounts} allowedTypes={["expense", "asset"]} /></Grid>
                  ) : null}
                  {tracksInventory ? (
                    <>
                      <Grid item xs={12} md={6}><AccountSelect label="Valor del inventario" value={form.inventoryAccountCode} onChange={(event) => change("inventoryAccountCode", event.target.value)} accounts={accounts} allowedTypes={["asset"]} /></Grid>
                      <Grid item xs={12} md={6}><AccountSelect label="Costo de lo vendido" value={form.costAccountCode} onChange={(event) => change("costAccountCode", event.target.value)} accounts={accounts} allowedTypes={["expense"]} /></Grid>
                    </>
                  ) : null}
                  {form.expenseEnabled || form.internalConsumptionEnabled ? (
                    <Grid item xs={12} md={6}><AccountSelect label="Dónde registrar el gasto" value={form.expenseAccountCode} onChange={(event) => change("expenseAccountCode", event.target.value)} accounts={accounts} allowedTypes={["expense"]} /></Grid>
                  ) : null}
                </Grid>
              ) : (
                <Alert severity="info" sx={{ py: 0.2 }}>Las cuentas contables todavía no están conectadas a esta vista.</Alert>
              )}

              {(form.sellable || form.purchasable || form.expenseEnabled) ? (
                activeTaxProfiles.length ? (
                  <TextField
                    select
                    size="small"
                    label="Impuesto aplicable"
                    value={form.taxProfileCode || ""}
                    onChange={(event) => change("taxProfileCode", event.target.value)}
                    fullWidth
                  >
                    <MenuItem value="">Sin impuesto configurado</MenuItem>
                    {activeTaxProfiles.map((profile) => (
                      <MenuItem key={profile.code} value={profile.code}>
                        {profile.name}{Number(profile.rate || 0) ? profile.calculationMode === "fixed" ? ` · importe fijo ${Number(profile.rate)}` : ` · ${Number(profile.rate)}%` : ""}
                      </MenuItem>
                    ))}
                  </TextField>
                ) : (
                  <Alert severity="info" sx={{ py: 0.2 }}>Todavía no hay perfiles de impuestos configurados para elegir.</Alert>
                )
              ) : null}

              {form.sellable ? (
                <Alert severity={selectedTax ? "success" : "info"} sx={{ py: 0.2 }}>
                  {selectedTax
                    ? `Este ítem usará ${selectedTax.name || "el perfil fiscal elegido"} al calcular impuestos.`
                    : "El ítem puede guardarse ahora y completar el impuesto cuando termines de configurar facturación."}
                  {" "}La emisión del comprobante requiere además un proveedor fiscal conectado al proyecto.
                </Alert>
              ) : null}
            </Stack>
          </Paper>
        )
      ) : null}
    </Stack>
  );
}
